/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { Location } from "react-router-dom";
import { generatePath } from "react-router-dom";
import { escapeReactRoute } from "./escapeReactParameter";
export function resolvePathWithSpaceId(to: Partial<Location> | string, spaceId: string | undefined): Partial<Location> | string {
    if (isLocationObject(to)) {
        return { ...to, pathname: resolveStringPathWithSpaceId(to.pathname!, spaceId) };
    }
    return resolveStringPathWithSpaceId(to, spaceId);
}
export function isLocationObject(to: Partial<Location> | string): to is Partial<Location> {
    return typeof to !== "string" && typeof to !== "undefined" && typeof to.pathname !== "undefined";
}
const FakeSpaceId = "Spaces-0";
export function resolveStringPathWithSpaceId(path: string, spaceId: string | null | undefined): string {
    // path contains space id parameter so it needs to be resolved even if space id is not provided
    spaceId = spaceId ? spaceId : FakeSpaceId;
    /**
     * generatePath is typically used for defining route templates for a Route component.
     * This means it also parses regexs out of the path into a token, and expects a value to be provided for this token.
     * We are only using generatePath to replace the spaceId token, so we don't want any other tokens to be parsed from this path.
     * For example, a route like "/:spaceId(Spaces-[0-9]+)?/releases/20210221.7.$(Rev:rr)" will have (Rev:rr) parsed as a regex token,
     * and generatePath will expect a value to be specified for it else it will throw an error.
     * To prevent this, we must escape regex special characters using a backslash which is done in escapeReactRoute.
     */
    const encodedPath = encodeAllPathElementsExceptSpaceID(path);
    const link = generatePath(encodedPath, { spaceId });
    return spaceId === FakeSpaceId ? link.replace(FakeSpaceId, "").replace("//", "/") : link;
}
/**
 * The only route parameter we can possibly have at this point is the space id, which is the first part of any URL.
 * Any other components of the URL that have characters special to react (like colons etc) must be literal and
 * not interpreted as react path parameters. So escape any part of the url that has special characters, leaving the
 * space id parameter in place.
 * @param path The path to escape
 */
function encodeAllPathElementsExceptSpaceID(path: string) {
    const segments = path.split("/");
    const firstPathSegment = path.indexOf("/");
    return segments
        .map((val, index) => {
        // The space id parameter is either the first or second array item depending on whether the path started with a slash
        if (index <= (firstPathSegment === 0 ? 2 : 1)) {
            return val;
        }
        return escapeReactRoute(val);
    })
        .join("/");
}
